import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { Send, Users, Image, FileText, Clock, CheckCircle, XCircle, Loader2, Download, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function MassMessaging() {
  const requestVersion = useRef(0);
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('compose'); // compose, history
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [reviewNeeded, setReviewNeeded] = useState([]);
  const [search, setSearch] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const visibleLeads = useMemo(() => leads.filter(lead =>
    `${lead.name} ${lead.phone}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())), [leads, search]);
  const selectAll = visibleLeads.length > 0 && visibleLeads.every(lead => selectedLeads.includes(lead.phone));

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchLeads = useCallback(async ({ background = false } = {}) => {
    const version = ++requestVersion.current;
    if (!background) setLoading(true);
    setRefreshing(true);
    setLoadError('');
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}/whatsapp/phone-list`, {
        cache: 'no-store',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (version !== requestVersion.current) return;
        setLeads(data.contacts || []);
        const currentPhones = new Set((data.contacts || []).map(lead => lead.phone));
        setSelectedLeads(previous => previous.filter(phone => currentPhones.has(phone)));
        setLastUpdated(new Date());
        setCoverage(data.coverage || null);
        setReviewNeeded(data.review_needed || []);
      } else {
        throw new Error('No se pudo recuperar la lista de teléfonos');
      }
    } catch (error) {
      if (version !== requestVersion.current) return;
      setLoadError('No se pudo recuperar la lista. Reintenta.');
      setLeads([]);
      setSelectedLeads([]);
      setReviewNeeded([]);
      setCoverage(null);
      setLastUpdated(null);
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchLeads();
    return () => { requestVersion.current += 1; };
  }, [fetchLeads]);
  useAutoRefresh(fetchLeads, sending);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}/whatsapp/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const exportCsv = (review = false) => {
    const rows = review ? reviewNeeded : (selectedLeads.length ? leads.filter(lead => selectedLeads.includes(lead.phone)) : visibleLeads);
    const cell = value => {
      let text = String(value ?? '');
      if (!/^\+\d{8,15}$/.test(text) && (/^[=+@\-]/.test(text.trimStart()) || /^[\t\r\n]/.test(text))) text = "'" + text;
      return '"' + text.replace(/"/g, '""') + '"';
    };
    const data = [['telefono_e164', 'nombre', 'correos', 'origen', 'estado', 'revision'],
      ...rows.map(lead => [lead.phone, lead.name, (lead.emails || []).join('; '), (lead.sources || []).join('; '), lead.status, lead.review_reason || ''])];
    const url = URL.createObjectURL(new Blob(['\uFEFF' + data.map(row => row.map(cell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = review ? 'AIMP_telefonos_por_revisar.csv' : 'AIMP_lista_telefonos.csv';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleSelectAll = () => {
    const visible = new Set(visibleLeads.map(lead => lead.phone));
    setSelectedLeads(previous => selectAll ? previous.filter(phone => !visible.has(phone)) : [...new Set([...previous, ...visible])]);
  };

  const handleSelectLead = phone => {
    setSelectedLeads(previous => previous.includes(phone) ? previous.filter(value => value !== phone) : [...previous, phone]);
  };

  const handleSend = async () => {
    if (selectedLeads.length === 0) {
      alert('Selecciona al menos un contacto');
      return;
    }
    if (!message.trim()) {
      alert('Escribe un mensaje');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      
      const response = await fetch(`${API}/api/clinic/${clinicId}/whatsapp/send-mass`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phones: selectedLeads,
          message: message,
          image_url: imageUrl || null,
          delay_seconds: 2.0
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ ${result.message}`);
        setMessage('');
        setImageUrl('');
        setSelectedLeads([]);
        fetchCampaigns();
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.detail || 'Error enviando mensajes'}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="mass-messaging-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📢 WhatsApp Masivo</h1>
        <p className="text-gray-600">Teléfonos recuperados de fichas y conversaciones, sin duplicados.</p>
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={fetchLeads} disabled={refreshing || sending} className="px-3 py-2 border rounded-lg bg-white text-gray-800 disabled:opacity-50"><RefreshCw className="w-4 h-4 inline mr-2" />Actualizar teléfonos</button>
          <button onClick={() => exportCsv()} disabled={loading || !visibleLeads.length} className="px-3 py-2 border rounded-lg bg-white text-gray-800 disabled:opacity-50"><Download className="w-4 h-4 inline mr-2" />Exportar CSV ({selectedLeads.length || visibleLeads.length})</button>
          {reviewNeeded.length > 0 && <button onClick={() => exportCsv(true)} disabled={loading} className="px-3 py-2 border rounded-lg bg-white text-gray-800">Exportar revisión ({reviewNeeded.length})</button>}
        </div>
        {coverage && <p className="text-sm text-gray-600 mt-3">{coverage.crm_leads} leads revisados · {leads.length} teléfonos únicos · {coverage.recovered_from_messages} adicionales recuperados de mensajes · {coverage.suppressed} excluidos por baja · {coverage.review_needed} por revisar.</p>}
        <p className="text-sm text-gray-600 mt-2" role="status">La lista se actualiza al abrir o volver a esta pestaña y cada 5 minutos mientras esté visible.{lastUpdated ? ` Última actualización: ${lastUpdated.toLocaleTimeString('es-MX')}.` : ''}</p>
        {loadError && <p role="alert" className="text-red-700 mt-3">{loadError}</p>}
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('compose')}
          className={`pb-3 px-4 font-medium ${activeTab === 'compose' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
          data-testid="tab-compose"
        >
          <Send className="w-4 h-4 inline mr-2" />
          Nuevo Envío
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`pb-3 px-4 font-medium ${activeTab === 'history' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
          data-testid="tab-history"
        >
          <Clock className="w-4 h-4 inline mr-2" />
          Historial
        </button>
      </div>

      {activeTab === 'compose' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact Selection */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">
                <Users className="w-5 h-5 inline mr-2" />
                Seleccionar Contactos
              </h2>
              <span className="text-sm text-gray-500">
                {selectedLeads.length} de {leads.length} seleccionados
              </span>
            </div>

            <input aria-label="Buscar nombre o teléfono" value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar nombre o teléfono" className="w-full px-3 py-2 border rounded-lg mb-3 text-gray-900 bg-white" />
            <div className="mb-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Seleccionar visibles ({visibleLeads.length})</span>
              </label>
            </div>

            <div className="max-h-80 overflow-y-auto space-y-2">
              {visibleLeads.map(lead => (
                <label
                  key={lead.id}
                  className="flex items-center p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(lead.phone)}
                    onChange={() => handleSelectLead(lead.phone)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{lead.name || 'Sin nombre'}</p>
                    <p className="text-xs text-gray-500">{lead.phone}</p>
                  </div>
                </label>
              ))}
              {loading ? <p className="text-gray-500 text-center py-8">Recuperando teléfonos de todo el historial…</p> : visibleLeads.length === 0 && (
                <p className="text-gray-500 text-center py-8">No hay contactos con teléfono</p>
              )}
            </div>
          </div>

          {/* Message Composition */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="font-semibold text-gray-900 mb-4">
              <FileText className="w-5 h-5 inline mr-2" />
              Componer Mensaje
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mensaje</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Escribe tu mensaje aquí..."
                  rows={5}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                  data-testid="message-input"
                />
                <p className="text-xs text-gray-500 mt-1">{message.length} caracteres</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Image className="w-4 h-4 inline mr-1" />
                  URL de Imagen (opcional)
                </label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                  data-testid="image-url-input"
                />
              </div>

              {imageUrl && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Vista previa:</p>
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="max-h-32 rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}

              <button
                onClick={handleSend}
                disabled={loading || sending || selectedLeads.length > 500 || selectedLeads.length === 0 || !message.trim()}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                data-testid="send-button"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Enviar a {selectedLeads.length} contactos
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Puedes exportar toda la lista. Cada envío admite hasta 500 contactos seleccionados.
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* Campaign History */
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mensaje</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contactos</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enviados</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {campaigns.map(campaign => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{formatDate(campaign.created_at)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{campaign.message}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{campaign.total_contacts}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className="text-green-600">{campaign.sent_count || 0}</span>
                    {campaign.failed_count > 0 && (
                      <span className="text-red-500 ml-1">/ {campaign.failed_count} fallidos</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {campaign.status === 'completed' ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Completado
                      </span>
                    ) : campaign.status === 'in_progress' ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Enviando...
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                        <XCircle className="w-3 h-3 mr-1" />
                        Error
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {campaigns.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No hay campañas anteriores
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

